import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Container, Heading, Muted, Screen, Skeleton, Stack, Text } from '@fluent/ui';
import { useAuth } from '../auth/authStore.js';
import { useDeckOverview } from '../flashcards/hooks.js';
import { AppBottomNav } from '../nav/AppBottomNav.js';
import './Flashcards.css';

/**
 * Flashcards home — the deck's front door.
 *
 * Layout language (bold pass, 2026-07-14): a hero "deck panel" carrying
 * the chameleon mascot holding flashcards (character-15 — drawn with Aa/Cc
 * cards in hand), today's set numbers, and a fat Start key. (The hero
 * showed the user's real card fronts until 2026-07-20 — variable text
 * broke mid-word inside the 78px minis; Tornike cut it and picked the
 * mascot from three proposed replacements.)
 * Below: three stat tiles (new / learning / known) and the deck browser.
 * Framing rules stay (FLASHCARDS.md): today's plan + time estimate only,
 * never the raw backlog.
 */
export function Flashcards() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [now] = useState(() => new Date());
  const { plan, entries, loaded } = useDeckOverview(now);

  if (!user) return null;

  const dueCount = plan.due.length;
  const newCount = plan.fresh.length;
  const totalToday = dueCount + newCount;
  const hasWork = totalToday > 0;
  // ~6s per review, ~15s per new card, rounded up to a minute.
  const minutes = Math.max(1, Math.ceil((dueCount * 6 + newCount * 15) / 60));

  return (
    <Screen>
      <Container size="sm">
        <Stack
          gap={6}
          style={{
            padding:
              'var(--space-6, 24px) 0 calc(var(--space-16, 64px) + 88px + env(safe-area-inset-bottom))',
          }}
        >
          <Stack gap={2}>
            <Heading level={1}>{t('flashcards.title')}</Heading>
            <Muted>{t('flashcards.subtitle')}</Muted>
          </Stack>

          {!loaded ? (
            <Skeleton shape="card" style={{ height: 260 }} />
          ) : entries.length === 0 ? (
            <div className="fluent-fc-home__hero" data-mode="empty">
              <CardFan mode="empty" />
              <Stack gap={2} align="center" style={{ textAlign: 'center' }}>
                <Heading level={3}>{t('flashcards.empty_title')}</Heading>
                <Text tone="muted">{t('flashcards.empty_body')}</Text>
              </Stack>
            </div>
          ) : hasWork ? (
            <div className="fluent-fc-home__hero">
              <CardFan mode="ready" />
              <div className="fluent-fc-home__mission">
                <span className="fluent-fc-home__mission-count">{totalToday}</span>
                <Stack gap={1}>
                  <Text weight="bold">{t('flashcards.today_title')}</Text>
                  <Text tone="muted" size="sm">
                    {dueCount > 0 && t('flashcards.today_due', { count: dueCount })}
                    {dueCount > 0 && newCount > 0 && ' · '}
                    {newCount > 0 && t('flashcards.today_new', { count: newCount })}
                    {' · '}
                    {t('flashcards.today_minutes', { count: minutes })}
                  </Text>
                </Stack>
              </div>
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={() => navigate('/flashcards/session')}
              >
                {t('flashcards.start')}
              </Button>
            </div>
          ) : (
            <div className="fluent-fc-home__hero" data-mode="done">
              <CardFan mode="done" />
              <Stack gap={2} align="center" style={{ textAlign: 'center' }}>
                <Heading level={3}>{t('flashcards.done_title')}</Heading>
                <Text tone="muted">
                  {plan.nextDueAt
                    ? t('flashcards.done_body_next', { when: formatNextDue(plan.nextDueAt, now, t) })
                    : t('flashcards.done_body')}
                </Text>
              </Stack>
            </div>
          )}

          {loaded && entries.length > 0 && (
            <>
              <div className="fluent-fc-home__stats">
                <StatTile tone="new" label={t('flashcards.stat_new')} value={plan.counts.newCount} />
                <StatTile tone="learning" label={t('flashcards.stat_learning')} value={plan.counts.learning} />
                <StatTile tone="known" label={t('flashcards.stat_known')} value={plan.counts.review} />
              </div>

              <Button
                variant="neutral"
                fullWidth
                onClick={() => navigate('/flashcards/browse')}
              >
                {t('flashcards.browse', { count: entries.length })}
              </Button>
            </>
          )}
        </Stack>
      </Container>
      <AppBottomNav />
    </Screen>
  );
}

/**
 * Hero visual. `ready` shows the chameleon mascot holding flashcards
 * (fixed art can't break the way variable-length card fronts did);
 * `done` shows a single check-stamped card; `empty` shows dashed ghost
 * slots waiting for the first completed lesson.
 */
function CardFan({ mode }: { mode: 'ready' | 'done' | 'empty' }) {
  if (mode === 'done') {
    return (
      <div className="fluent-fc-home__fan" aria-hidden="true">
        <div className="fluent-fc-home__mini" data-pos="1" data-done="true">
          <span className="fluent-fc-home__mini-check">✓</span>
        </div>
      </div>
    );
  }

  if (mode === 'empty') {
    return (
      <div className="fluent-fc-home__fan" aria-hidden="true">
        <div className="fluent-fc-home__mini" data-pos="0" data-ghost="true" />
        <div className="fluent-fc-home__mini" data-pos="1" data-ghost="true" />
        <div className="fluent-fc-home__mini" data-pos="2" data-ghost="true" />
      </div>
    );
  }

  return (
    <div className="fluent-fc-home__fan" data-mascot="true" aria-hidden="true">
      <img className="fluent-fc-home__mascot" src="/characters/character-15.png" alt="" />
    </div>
  );
}

function StatTile({ tone, label, value }: { tone: 'new' | 'learning' | 'known'; label: string; value: number }) {
  return (
    <div className="fluent-fc-home__stat" data-tone={tone}>
      <span className="fluent-fc-home__stat-value">{value}</span>
      <Muted>{label}</Muted>
    </div>
  );
}

function formatNextDue(
  nextDueAt: Date,
  now: Date,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const sameDay = nextDueAt.toDateString() === now.toDateString();
  if (sameDay) {
    const hh = String(nextDueAt.getHours()).padStart(2, '0');
    const mm = String(nextDueAt.getMinutes()).padStart(2, '0');
    return t('flashcards.next_today', { time: `${hh}:${mm}` });
  }
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (nextDueAt.toDateString() === tomorrow.toDateString()) return t('flashcards.next_tomorrow');
  const days = Math.ceil((nextDueAt.getTime() - now.getTime()) / 86_400_000);
  return t('flashcards.next_days', { count: days });
}
